import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatChartTickValue } from '../../utils/chartFormatUtils';
import {
  getInterveneMetricLabelOverrides,
  getInterveneSectionTitles,
} from '../../utils/intervenePreviewLabels';
import {
  formatDurationSeconds,
  isDurationMetricId,
} from '../../utils/interveneFilters';

const SECTIONS = [
  {
    id: 'performance',
    titleKey: 'performance',
    icon: '⏱',
    metricIds: [
      'avg_agent_response',
      'median_agent_response',
      'avg_first_agent_response',
      'avg_customer_response',
      'agent_reply_count',
    ],
  },
  {
    id: 'volume',
    titleKey: 'volume',
    icon: '💬',
    metricIds: ['messages_total', 'inbound_count', 'outbound_human_count', 'session_count'],
  },
  {
    id: 'delivery',
    titleKey: 'delivery',
    icon: '📦',
    metricIds: ['delivered_count', 'read_count', 'failed_count'],
  },
];

function MetricTile({
  item,
  labelOverrides,
  highlight = false,
  focusedUser = '',
  dense = false,
}) {
  const display = isDurationMetricId(item.id)
    && item.value !== null
    && item.value !== undefined
    && Number.isFinite(Number(item.value))
    ? formatDurationSeconds(item.value)
    : item.display || formatChartTickValue(item.value) || '0';
  const label = labelOverrides[item.id] || item.label;
  const replyCaption = focusedUser
    ? `Paired replies in ${focusedUser.split(' ')[0] || focusedUser}'s chats`
    : 'Paired replies';

  const isFailed = item.id === 'failed_count' && Number(item.value) > 0;
  const isDelivered = item.id === 'delivered_count';
  const isRead = item.id === 'read_count';

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: dense ? 1.25 : 1.5,
        minWidth: dense ? 75 : 95,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: dense ? 1.5 : 2,
        borderColor: highlight
          ? 'primary.main'
          : isFailed
            ? '#ef4444'
            : 'divider',
        bgcolor: highlight
          ? alpha(theme.palette.primary.main, 0.07)
          : isFailed
            ? '#fef2f2'
            : 'background.paper',
        boxShadow: highlight ? '0 2px 6px rgba(0, 168, 132, 0.12)' : 'none',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: highlight ? 'primary.main' : 'text.secondary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        },
      })}
    >
      <Box sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            fontWeight: 600,
            fontSize: dense ? '0.7rem' : '0.76rem',
          }}
          title={label}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <Typography
          variant={highlight && !dense ? 'h5' : dense ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            fontSize: dense ? '1.15rem' : highlight ? '1.4rem' : '1.3rem',
            color: highlight
              ? 'primary.main'
              : isDelivered
                ? '#059669'
                : isRead
                  ? '#7c3aed'
                  : isFailed
                    ? '#dc2626'
                    : 'text.primary',
          }}
        >
          {display}
        </Typography>
        {!dense && item.count != null && item.display ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ mt: 0.5, display: 'block', fontSize: '0.68rem', lineHeight: 1.2 }}
          >
            {replyCaption} · {formatChartTickValue(item.count)}
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

function SectionBlock({
  section,
  items,
  sectionTitles,
  labelOverrides,
  focusedUserDisplayName,
  dense,
  sectionCount,
}) {
  const isOnlyOne = sectionCount === 1;
  const isPerf = section.id === 'performance';
  const isVol = section.id === 'volume';
  const isDel = section.id === 'delivery';

  // Responsive flex distribution:
  // - If single section: take full width (100%)
  // - If performance section: span full width (100%) on its own row so 5 metrics have ample room
  // - If volume and delivery: share the row side-by-side with proportional flex (1.15 for 4 items vs 1 for 3 items)
  let flexStyle = '1 1 240px';
  if (isOnlyOne || isPerf) {
    flexStyle = '1 1 100%';
  } else if (isVol) {
    flexStyle = '1.15 1 240px';
  } else if (isDel) {
    flexStyle = '1 1 200px';
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        flex: flexStyle,
        p: dense ? 1.25 : 1.5,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.015),
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{
          letterSpacing: 0.8,
          fontWeight: 700,
          mb: dense ? 1 : 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: dense ? '0.65rem' : '0.72rem',
          lineHeight: 1,
        }}
      >
        <span>{section.icon}</span>
        <span>{sectionTitles[section.titleKey]}</span>
      </Typography>
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gap: dense ? 1 : 1.25,
          gridTemplateColumns: dense
            ? 'repeat(auto-fit, minmax(75px, 1fr))'
            : 'repeat(auto-fit, minmax(100px, 1fr))',
          alignItems: 'stretch',
        }}
      >
        {items.map((item, index) => (
          <MetricTile
            key={item.id}
            item={item}
            labelOverrides={labelOverrides}
            focusedUser={focusedUserDisplayName}
            highlight={section.id === 'performance' && index === 0}
            dense={dense}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function InterveneMetricSummary({
  metrics = [],
  focusedUserDisplayName = '',
  compact = false,
}) {
  const byId = useMemo(
    () => Object.fromEntries(metrics.map((item) => [item.id, item])),
    [metrics]
  );
  const labelOverrides = useMemo(
    () => getInterveneMetricLabelOverrides(focusedUserDisplayName),
    [focusedUserDisplayName]
  );
  const sectionTitles = useMemo(
    () => getInterveneSectionTitles(focusedUserDisplayName),
    [focusedUserDisplayName]
  );

  if (!metrics.length) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          0
        </Typography>
      </Box>
    );
  }

  const sections = SECTIONS.map((section) => ({
    section,
    items: section.metricIds.map((id) => byId[id]).filter(Boolean),
  })).filter(({ items }) => items.length);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: compact ? 1.25 : 1.75,
          width: '100%',
          boxSizing: 'border-box',
          alignItems: 'stretch',
        }}
      >
        {sections.map(({ section, items }) => (
          <SectionBlock
            key={section.id}
            section={section}
            items={items}
            sectionTitles={sectionTitles}
            labelOverrides={labelOverrides}
            focusedUserDisplayName={focusedUserDisplayName}
            dense={compact}
            sectionCount={sections.length}
          />
        ))}
      </Box>
    </Box>
  );
}
